import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useDistanceUnit } from "../contexts/DistanceUnitContext";
import { useNotifications } from "../contexts/NotificationContext";
import { API_BASE_URL } from "../config";
import Sidebar from "./Sidebar";
import "./Friends.css";

// Import assets
import iconUsersAdd from "../assets/icon-users-add.svg";
import iconSearchFriends from "../assets/icon-search-friends.svg";
import iconTrophyLeaderboard from "../assets/icon-trophy-leaderboard.svg";
import iconTrophyGold from "../assets/icon-trophy-gold.svg";
import iconFlameSmall from "../assets/icon-flame-small.svg";
import iconFlameStreak from "../assets/icon-flame-streak.svg";
import iconMenu from "../assets/icon-menu.svg";
import iconSearch from "../assets/icon-search.svg";
import NotificationBell from "./NotificationBell";
import ProfilePicture from "./ProfilePicture";
import FriendProfileModal from "./FriendProfileModal";

const Friends = ({ user, onLogout }) => {
  const [friendsStats, setFriendsStats] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [sentRequests, setSentRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [friendsSearchQuery, setFriendsSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('friends');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [statusMessage, setStatusMessage] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const { convertDistance, getUnitLabel } = useDistanceUnit();
  const navigate = useNavigate();
  const { refreshNotifications } = useNotifications();

  const fetchFriends = useCallback(async () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const response = await axios.get(`${API_BASE_URL}/friends`, {
        params: { email: storedUser.email },
        withCredentials: true
      });

      setPendingRequests(response.data.pending_requests || []);
      setSentRequests(response.data.sent_requests || []);
      try {
        await refreshNotifications();
      } catch (refreshError) {
        console.error("Unable to refresh notifications after fetching friends:", refreshError);
      }
      setLoading(false);
      return response.data;
    } catch (error) {
      console.error("Error fetching friends:", error);
      setPendingRequests([]);
      setSentRequests([]);
      setStatusMessage({
        type: 'error',
        text: error.response?.data?.error || "Unable to load friends. Please try again."
      });
      setLoading(false);
      return null;
    }
  }, [refreshNotifications]);

  const fetchFriendsStats = useCallback(async () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const response = await axios.get(`${API_BASE_URL}/friends/stats`, {
        params: { email: storedUser.email },
        withCredentials: true
      });

      setFriendsStats(response.data.friends_stats || []);
    } catch (error) {
      console.error("Error fetching friends stats:", error);
      setFriendsStats([]);
    }
  }, []);

  useEffect(() => {
    fetchFriends();
    fetchFriendsStats();
    fetchProfile();
  }, [fetchFriends, fetchFriendsStats]);

  const fetchProfile = async () => {
    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const response = await axios.get(`${API_BASE_URL}/profile`, {
        params: { email: storedUser.email },
        withCredentials: true
      });
      setProfileData(response.data.profile);
    } catch (error) {
      console.error("Error fetching profile:", error);
      if (error.response?.status === 404) {
        console.warn("Profile not found - 404. This is OK for friends page.");
        // Don't show an error to user as profile picture is optional
      }
    }
  };

  useEffect(() => {
    if (!statusMessage) {
      return;
    }
    const timeout = setTimeout(() => setStatusMessage(null), 4000);
    return () => clearTimeout(timeout);
  }, [statusMessage]);

  const handleSearch = async (query) => {
    setSearchQuery(query);

    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const response = await axios.get(`${API_BASE_URL}/friends/search`, {
        params: { query, email: storedUser.email },
        withCredentials: true
      });

      setSearchResults(response.data.users);
    } catch (error) {
      console.error("Error searching users:", error);
    }
  };

  const sendFriendRequest = async (targetUser) => {
    if (!targetUser?.id) {
      return;
    }

    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      await axios.post(`${API_BASE_URL}/friends/request`, {
        email: storedUser.email,
        friend_id: targetUser.id
      }, { withCredentials: true });

      const displayName = targetUser.username || targetUser.email || "this runner";
      setStatusMessage({
        type: 'success',
        text: `Friend request sent to ${displayName}.`
      });

      setSearchQuery("");
      setSearchResults([]);
      await fetchFriends();
    } catch (error) {
      console.error("Error sending friend request:", error);
      setStatusMessage({
        type: 'error',
        text: error.response?.data?.error || "Failed to send friend request"
      });
    }
  };

  const respondToRequest = async (friendshipId, action) => {
    const matchingRequest = pendingRequests.find(
      (request) => request.friendship_id === friendshipId
    );
    const requesterName = matchingRequest?.requester_username || "this runner";

    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      await axios.post(`${API_BASE_URL}/friends/respond`, {
        email: storedUser.email,
        friendship_id: friendshipId,
        action
      }, { withCredentials: true });

      if (action === 'accept') {
        setStatusMessage({
          type: 'success',
          text: `You are now connected with ${requesterName}.`
        });
      } else {
        setStatusMessage({
          type: 'info',
          text: `Friend request from ${requesterName} dismissed.`
        });
      }

      await fetchFriends();
    } catch (error) {
      console.error("Error responding to request:", error);
      setStatusMessage({
        type: 'error',
        text: "Failed to respond to friend request"
      });
    }
  };
  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const convertMilesToUnit = (miles) => {
    return parseFloat(convertDistance(miles * 1609.34)).toFixed(1);
  };

  const filteredFriendsStats = friendsStats.filter(stat =>
    `${stat.first_name || ''} ${stat.last_name || ''} ${stat.username || ''}`
      .toLowerCase()
      .includes(friendsSearchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="friends-page">
        <Sidebar isOpen={sidebarOpen} />
        <div className={`friends-main-container ${!sidebarOpen ? 'sidebar-closed' : ''}`}>
          <div className="friends-loading">Loading friends...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="friends-page">
      <Sidebar isOpen={sidebarOpen} />

      <div className={`friends-main-container ${!sidebarOpen ? 'sidebar-closed' : ''}`}>
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-left">
            <button className="icon-button" onClick={toggleSidebar}>
              <img src={iconMenu} alt="Menu" />
            </button>
            <h1 className="header-title">Runstreak Dashboard</h1>
          </div>
          <div className="header-right">
            <div className="header-search-container">
              <img src={iconSearch} alt="Search" className="search-icon-header" />
              <input
                type="text"
                placeholder="Search activities..."
                className="header-search-input"
              />
            </div>
            <NotificationBell />
            <button className="logout-button" onClick={onLogout}>Logout</button>
            <div onClick={() => navigate('/profile')} style={{ cursor: 'pointer' }}>
              <ProfilePicture
                profilePictureUrl={profileData?.profile_picture_url}
                firstName={profileData?.first_name}
                lastName={profileData?.last_name}
                email={user?.email}
                size="small"
                showInitials={true}
              />
            </div>
          </div>
        </div>

        {statusMessage && (
          <div className={`friends-status friends-status--${statusMessage.type || 'info'}`}>
            {statusMessage.text}
          </div>
        )}

        {/* Main Content */}
        <div className="friends-content">
          {/* Page Header */}
          <div className="friends-header">
            <h1>Friends</h1>
            <button className="find-friends-button">
              <img src={iconUsersAdd} alt="Find Friends" />
              <span>Find Friends</span>
            </button>
          </div>

          {/* Tabs */}
          <div className="friends-tabs-container">
            <div className="friends-tabs">
              <button
                className={`friends-tab ${activeTab === 'friends' ? 'active' : ''}`}
                onClick={() => setActiveTab('friends')}
              >
                Friends ({friendsStats.length})
              </button>
              <button
                className={`friends-tab ${activeTab === 'requests' ? 'active' : ''}`}
                onClick={() => setActiveTab('requests')}
              >
                Requests ({pendingRequests.length})
                {pendingRequests.length > 0 && (
                  <span className="tab-badge">{pendingRequests.length}</span>
                )}
              </button>
              <button
                className={`friends-tab ${activeTab === 'discover' ? 'active' : ''}`}
                onClick={() => setActiveTab('discover')}
              >
                Discover
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="friends-tab-content">
            {activeTab === 'friends' && (
              <>
                {/* Search Bar */}
                <div className="friends-search-container">
                  <img src={iconSearchFriends} alt="Search" className="friends-search-icon" />
                  <input
                    type="text"
                    placeholder="Search friends..."
                    className="friends-search-input"
                    value={friendsSearchQuery}
                    onChange={(e) => setFriendsSearchQuery(e.target.value)}
                  />
                </div>

                {/* Weekly Leaderboard Card */}
                <div className="friends-leaderboard-card">
                  <div className="friends-card-header">
                    <div className="friends-card-title">
                      <img src={iconTrophyLeaderboard} alt="Trophy" />
                      <h2>Weekly Leaderboard</h2>
                    </div>
                    <p className="friends-card-description">
                      Compare your running streaks with friends
                    </p>
                  </div>

                  <div className="friends-leaderboard-list">
                    {filteredFriendsStats.length === 0 ? (
                      <p className="friends-empty-message">
                        No friends found. Add friends to see the leaderboard!
                      </p>
                    ) : (
                      filteredFriendsStats.map((stat, index) => (
                        <div
                          key={stat.user_id}
                          className="friends-leaderboard-item"
                          onClick={() => setSelectedUserId(stat.user_id)}
                        >
                          {/* Rank */}
                          <div className="friends-rank">
                            {index === 0 ? (
                              <img src={iconTrophyGold} alt="Leader" className="trophy-icon" />
                            ) : (
                              <span className="rank-number">{index + 1}</span>
                            )}
                          </div>

                          {/* Avatar */}
                          <div className="friends-avatar">
                            <ProfilePicture
                              profilePictureUrl={stat.profile_picture_url}
                              firstName={stat.first_name}
                              lastName={stat.last_name}
                              email={stat.email || stat.username}
                              size="medium"
                              className="friends-avatar-image"
                              showInitials={true}
                            />
                          </div>

                          {/* User Info */}
                          <div className="friends-user-info">
                            <div className="friends-user-name-row">
                              <span className="friends-user-name">
                                {stat.first_name && stat.last_name
                                  ? `${stat.first_name} ${stat.last_name}`
                                  : stat.username}
                              </span>
                              {index === 0 && (
                                <span className="leader-badge">Leader</span>
                              )}
                            </div>
                            <div className="friends-user-meta">
                              <span className="friends-meta-item">
                                <img src={iconFlameSmall} alt="Streak" className="meta-icon" />
                                {stat.current_streak} days
                              </span>
                              <span className="friends-meta-item">
                                {stat.weekly_distance ?
                                  `${convertMilesToUnit(stat.weekly_distance)}${getUnitLabel()} this week` :
                                  'No data this week'}
                              </span>
                            </div>
                          </div>

                          {/* Streak Display */}
                          <div className="friends-streak-display">
                            <div className="streak-number">{stat.current_streak}</div>
                            <div className="streak-label">day streak</div>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* Friend Cards Grid */}
                <div className="friends-cards-grid">
                  {filteredFriendsStats.slice(0, 6).map((stat) => (
                    <div
                      key={stat.user_id}
                      className="friend-card"
                      onClick={() => setSelectedUserId(stat.user_id)}
                    >
                      <div className="friend-card-header">
                        <div className="friend-card-avatar">
                          <ProfilePicture
                            profilePictureUrl={stat.profile_picture_url}
                            firstName={stat.first_name}
                            lastName={stat.last_name}
                            email={stat.email || stat.username}
                            size="large"
                            className="friend-card-avatar-image"
                            showInitials={true}
                          />
                        </div>
                        <div className="friend-card-info">
                          <h3>
                            {stat.first_name && stat.last_name
                              ? `${stat.first_name} ${stat.last_name}`
                              : stat.username}
                          </h3>
                          <p>{stat.longest_streak || 0} day streak</p>
                        </div>
                      </div>
                      <div className="friend-card-stats">
                        <div className="friend-stat-row">
                          <span className="stat-label">Current Streak</span>
                          <div className="stat-value-with-icon">
                            <img src={iconFlameStreak} alt="Streak" className="stat-icon" />
                            <span className="friend-stat-value">{stat.current_streak} days</span>
                          </div>
                        </div>
                        <div className="friend-stat-row">
                          <span className="stat-label">This Week</span>
                          <span className="friend-stat-value">
                            {stat.weekly_distance ?
                              `${convertMilesToUnit(stat.weekly_distance)}${getUnitLabel()}` :
                              'N/A'}
                          </span>
                        </div>
                        <div className="friend-progress-bar">
                          <div
                            className="friend-progress-fill"
                            style={{
                              width: stat.weekly_distance ?
                                `${Math.min((parseFloat(convertMilesToUnit(stat.weekly_distance)) / 50) * 100, 100)}%` :
                                '0%'
                            }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}

            {activeTab === 'requests' && (
              <div className="friends-requests-content">
                {/* Pending Requests */}
                {pendingRequests.length > 0 && (
                  <div className="friends-requests-section">
                    <h2>Friend Requests ({pendingRequests.length})</h2>
                    {pendingRequests.map(request => (
                      <div key={request.friendship_id} className="friends-request-item">
                        <div>
                          <strong>{request.requester_username}</strong> wants to be your friend
                        </div>
                        <div className="request-actions">
                          <button
                            onClick={() => respondToRequest(request.friendship_id, 'accept')}
                            className="request-accept-btn"
                          >
                            Accept
                          </button>
                          <button
                            onClick={() => respondToRequest(request.friendship_id, 'reject')}
                            className="request-reject-btn"
                          >
                            Reject
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Sent Requests */}
                {sentRequests.length > 0 && (
                  <div className="friends-requests-section">
                    <h2>Sent Requests ({sentRequests.length})</h2>
                    {sentRequests.map(request => (
                      <div key={request.friendship_id} className="friends-sent-request-item">
                        <div>
                          Request sent to <strong>{request.addressee_username}</strong>
                          <p>Pending...</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {pendingRequests.length === 0 && sentRequests.length === 0 && (
                  <div className="friends-empty-state">
                    <p>No pending friend requests</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'discover' && (
              <div className="friends-discover-content">
                <div className="friends-discover-search">
                  <h2>Find Friends</h2>
                  <input
                    type="text"
                    placeholder="Search by username..."
                    value={searchQuery}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="discover-search-input"
                  />

                  {searchResults.length > 0 && (
                    <div className="discover-results">
                      {searchResults.map((result) => (
                        <div key={result.id} className="discover-result-item">
                          <div>
                            <strong>{result.username}</strong>
                            <p>{result.email}</p>
                          </div>
                          <button
                            onClick={() => sendFriendRequest(result)}
                            className="discover-add-btn"
                          >
                            Add Friend
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Friend Profile Modal */}
      {selectedUserId && (
        <FriendProfileModal
          userId={selectedUserId}
          onClose={() => setSelectedUserId(null)}
        />
      )}
    </div>
  );
};

export default Friends;



